import React, { useState } from "react";
import { Modal, Tabs, Input, Button, message, Tooltip } from "antd";
import {
  BankOutlined,
  SafetyCertificateOutlined,
  UploadOutlined,
  LockOutlined,
  CheckOutlined,
  EditOutlined,
  ThunderboltOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { getCompanySeal, extractStampFromImage } from "../utils/sealGenerator";
import SignatureModal from "../components/SignatureModal";

const normalizeSealImage = (source) => {
  if (!source?.startsWith("data:image/svg+xml")) return Promise.resolve(source);

  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      canvas.getContext("2d").drawImage(image, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    image.onerror = reject;
    image.src = source;
  });
};

export default function CompanyModal({ company, setCompany, setShowCompanyModal, signatoryConfig, saveSignatoryConfig }) {
  const [activeTab, setActiveTab] = useState("info");
  const [pinCode, setPinCode] = useState("");
  const [customStamp, setCustomStamp] = useState(signatoryConfig?.sealImage || company?.stampImage || null);
  const [rawUploadedStamp, setRawUploadedStamp] = useState(null);
  const [managerSignature, setManagerSignature] = useState(signatoryConfig?.managerSignature || null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [saving, setSaving] = useState(false);
  const [processingStamp, setProcessingStamp] = useState(false);

  const currentSeal = customStamp;

  const handleUploadStamp = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      message.error("Vui lòng chọn file hình ảnh (.png, .jpg, .jpeg)!");
      return;
    }
    const reader = new FileReader();
    reader.onload = async (event) => {
      const rawData = event.target.result;
      setRawUploadedStamp(rawData);
      setProcessingStamp(true);
      try {
        // Tự động phân tích và tách nền giấy trắng nếu là ảnh chụp/scan
        const cleanPng = await extractStampFromImage(rawData, { autoCrop: true, threshold: 20 });
        setCustomStamp(cleanPng);
        message.success("Đã tải ảnh lên và tự động tách nền giấy thành con dấu trong suốt!");
      } catch (err) {
        console.error("Lỗi tự động tách nền:", err);
        setCustomStamp(rawData);
        message.success("Đã tải ảnh con dấu công ty lên!");
      } finally {
        setProcessingStamp(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAutoCleanBackground = async () => {
    const sourceToClean = rawUploadedStamp || customStamp;
    if (!sourceToClean) return;
    setProcessingStamp(true);
    try {
      const cleanPng = await extractStampFromImage(sourceToClean, { autoCrop: true, threshold: 20 });
      setCustomStamp(cleanPng);
      message.success("Đã bóc tách nền giấy và căn chỉnh con dấu thành công!");
    } catch (err) {
      message.error("Không thể tự động tách nền từ ảnh này.");
    } finally {
      setProcessingStamp(false);
    }
  };

  const handleSave = async () => {
    if (!managerSignature) {
      message.warning("Vui lòng cấu hình chữ ký cá nhân của Giám đốc!");
      return;
    }
    if (!signatoryConfig?.configured && !pinCode.trim()) {
      message.warning("Vui lòng thiết lập PIN bảo mật khi cấu hình chữ ký và con dấu lần đầu!");
      return;
    }

    setSaving(true);
    try {
      const sealImage = customStamp ? await normalizeSealImage(customStamp) : null;
      await saveSignatoryConfig({
        managerSignature,
        sealImage,
        managerName: company?.representative || signatoryConfig?.managerName || "",
        managerTitle: company?.repTitle || signatoryConfig?.managerTitle || "Giám đốc",
        pinCode: pinCode.trim() || null,
      });

      const companyInfo = { ...company };
      delete companyInfo.sealPin;
      delete companyInfo.stampImage;
      setCompany(companyInfo);
      localStorage.setItem("COMPANY_CONFIG_CONTRACT", JSON.stringify(companyInfo));
      message.success("Đã lưu cấu hình chữ ký và con dấu đại diện lên hệ thống!");
      setShowCompanyModal(false);
    } catch (error) {
      message.error(error?.response?.data?.message || "Lưu cấu hình chữ ký và con dấu thất bại!");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={true}
      onCancel={() => setShowCompanyModal(false)}
      width={540}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <BankOutlined style={{ color: "#1677ff", fontSize: 20 }} />
          <span style={{ fontWeight: 800, fontSize: 16 }}>Cấu hình Doanh nghiệp & Con dấu</span>
        </div>
      }
      footer={[
        <Button key="close" onClick={() => setShowCompanyModal(false)}>
          Đóng
        </Button>,
        <Button key="save" type="primary" icon={<CheckOutlined />} loading={saving} onClick={handleSave} style={{ fontWeight: 600 }}>
          Lưu thay đổi
        </Button>,
      ]}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "info",
            label: (
              <span>
                <BankOutlined /> Thông tin pháp nhân
              </span>
            ),
            children: (
              <div style={{ padding: "8px 0" }}>
                {[
                  { key: "companyName", label: "Tên công ty" },
                  { key: "companyAddress", label: "Địa chỉ trụ sở" },
                  { key: "representative", label: "Người đại diện theo PL" },
                  { key: "repTitle", label: "Chức vụ người đại diện" },
                  { key: "repIdNumber", label: "Số CCCD/Hộ chiếu người đại diện" },
                  { key: "repIdDate", label: "Ngày cấp CCCD người đại diện" },
                  { key: "certificate", label: "Giấy phép KD / Mã số thuế" },
                  { key: "phone", label: "Số điện thoại" },
                ].map((f) => (
                  <div key={f.key} style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 4 }}>
                      {f.label}
                    </label>
                    <Input
                      value={company[f.key]}
                      onChange={(e) => setCompany((c) => ({ ...c, [f.key]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            ),
          },
          {
            key: "stamp",
            label: (
              <span>
                <SafetyCertificateOutlined /> Chữ ký & Con dấu
              </span>
            ),
            children: (
              <div style={{ padding: "8px 0", textAlign: "center" }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 10, textAlign: "left" }}>
                  1. Chữ ký cá nhân của người đại diện:
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: 12, border: "1px solid #cbd5e1", borderRadius: 8, marginBottom: 18, background: "#f8fafc" }}>
                  {managerSignature ? (
                    <img src={managerSignature} alt="Chữ ký người đại diện" style={{ maxWidth: 220, height: 56, objectFit: "contain" }} />
                  ) : (
                    <span style={{ color: "#64748b", fontSize: 12 }}>Chưa cấu hình chữ ký</span>
                  )}
                  <Button icon={<EditOutlined />} onClick={() => setShowSignaturePad(true)}>
                    {managerSignature ? "Cập nhật chữ ký" : "Tạo chữ ký"}
                  </Button>
                </div>

                <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 8, textAlign: "left" }}>
                  2. Hình ảnh con dấu mộc đỏ công ty:
                </div>

                {/* Hướng dẫn tiện ích */}
                <div style={{ textAlign: "left", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "8px 12px", marginBottom: 12, fontSize: 11.5, color: "#166534", lineHeight: 1.5 }}>
                  <InfoCircleOutlined style={{ marginRight: 6, color: "#15803d" }} />
                  <strong>Lưu ý:</strong> Sếp chỉ cần lấy con dấu thật đóng lên giấy trắng rồi dùng điện thoại chụp ảnh gửi lên, hệ thống sẽ <strong>tự động tách nền giấy thành PNG trong suốt</strong> ngay lập tức.
                </div>

                {/* Box xem trước con dấu với nền ca-rô trong suốt */}
                <div
                  style={{
                    display: "inline-block",
                    padding: 14,
                    border: currentSeal ? "2px dashed #dc2626" : "2px dashed #cbd5e1",
                    borderRadius: 12,
                    background: "repeating-conic-gradient(#e2e8f0 0% 25%, #ffffff 0% 50%) 50% / 16px 16px",
                    marginBottom: 12,
                    boxShadow: "inset 0 0 8px rgba(0,0,0,0.05)",
                    minWidth: 160,
                    minHeight: 160,
                  }}
                >
                  {currentSeal ? (
                    <img src={currentSeal} alt="Company Seal" style={{ width: 140, height: 140, objectFit: "contain", display: "block" }} />
                  ) : (
                    <div style={{ width: 140, height: 140, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#64748b", fontSize: 12 }}>
                      <SafetyCertificateOutlined style={{ fontSize: 32, marginBottom: 8, color: "#94a3b8" }} />
                      Chưa tải lên ảnh con dấu
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 14px",
                      background: "#f1f5f9",
                      border: "1px solid #cbd5e1",
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    <UploadOutlined /> Tải ảnh con dấu (kể cả ảnh chụp điện thoại)
                    <input type="file" accept="image/*" hidden onChange={handleUploadStamp} />
                  </label>

                  {customStamp && (
                    <Tooltip title="Tự động lọc nền giấy trắng & căn chỉnh con dấu vuông vắn">
                      <Button
                        size="small"
                        icon={<ThunderboltOutlined />}
                        loading={processingStamp}
                        onClick={handleAutoCleanBackground}
                        style={{ borderColor: "#f59e0b", color: "#b45309", background: "#fef3c7" }}
                      >
                        Tách nền tự động
                      </Button>
                    </Tooltip>
                  )}

                  {customStamp && (
                    <Button
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        setCustomStamp(null);
                        setRawUploadedStamp(null);
                      }}
                    >
                      Xóa con dấu này
                    </Button>
                  )}


                </div>

                {/* Thiết lập mã PIN con dấu */}
                <div style={{ textAlign: "left", marginTop: 18 }}>
                  <label style={{ display: "block", fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 4 }}>
                    3. PIN cấu hình chữ ký và con dấu:
                  </label>
                  <Input.Password
                    prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
                    placeholder={signatoryConfig?.configured ? "Để trống nếu không đổi PIN" : "Nhập PIN bảo mật lần đầu"}
                    value={pinCode}
                    onChange={(e) => setPinCode(e.target.value)}
                    maxLength={12}
                    style={{ borderRadius: 6 }}
                  />
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                    * PIN được lưu dạng hash ở backend. Sau khi cấu hình, người có quyền chỉ cần xác nhận duyệt từng hợp đồng.
                  </div>
                </div>
              </div>
            ),
          },
        ]}
      />
      <SignatureModal
        open={showSignaturePad}
        onCancel={() => setShowSignaturePad(false)}
        onConfirm={(signature) => {
          setManagerSignature(signature);
          setShowSignaturePad(false);
        }}
        title="Cấu hình chữ ký của người đại diện"
        signerName={company?.representative}
        signerRole={company?.repTitle || "Giám đốc"}
      />
    </Modal>
  );
}
